import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, Clock, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { kioskService, productService } from '../services/api';
import { toast } from 'sonner';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { location, requestLocation, hasLocation } = useLocation();
  const [kiosks, setKiosks] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role-based redirect: if user is partner or admin, send them to their dashboard
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (['kiosk_owner', 'kiosk_staff'].includes(user.role)) {
        navigate('/partner/dashboard', { replace: true });
      } else if (user.role === 'city_hall') {
        navigate('/cityhall/dashboard', { replace: true });
      } else if (user.role === 'brand') {
        navigate('/brand/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    loadData();
  }, [location]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load nearby kiosks if location available
      let kiosksData = [];
      if (location) {
        kiosksData = await kioskService.getNearby(
          location.latitude,
          location.longitude
        );
      } else {
        kiosksData = await kioskService.list(null, true);
      }

      setKiosks(kiosksData.slice(0, 5));

      // Load featured products
      const productsData = await productService.list(null, null, true);
      const featured = productsData.filter((p) => p.is_featured).slice(0, 6);
      setFeaturedProducts(featured);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableLocation = () => {
    requestLocation();
    toast.success('Localização ativada!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" text={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <ErrorMessage message={error} onRetry={loadData} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-secondary p-6 rounded-b-3xl shadow-lg">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-white">
                {t('home.greeting')}, {user?.full_name?.split(' ')[0] || 'Visitante'}! 🌊
              </h1>
              <p className="text-white/90 text-sm mt-1">
                {t('home.subtitle')}
              </p>
            </div>
            <img
              src="/beachpay-icon.jpg"
              alt="Beach Pay"
              className="w-12 h-12"
            />
          </div>

          {!hasLocation && (
            <button
              data-testid="enable-location-button"
              onClick={handleEnableLocation}
              className="w-full bg-white/20 backdrop-blur-sm border border-white/30 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/30 transition-colors active:scale-95"
            >
              <MapPin className="w-5 h-5" />
              {t('home.enableLocation')}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 mt-6 space-y-6">
        {/* Banner */}
        <div className="relative rounded-2xl overflow-hidden shadow-md">
          <img
            src="https://images.unsplash.com/photo-1536869338989-e7ffd2297454"
            alt="Praia"
            className="w-full h-40 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
            <div>
              <h3 className="font-['Outfit'] text-white text-lg font-bold">
                {t('home.summer')}
              </h3>
              <p className="text-white/90 text-sm">{t('home.summerSub')}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2">
          <button
            data-testid="qa-map"
            onClick={() => navigate('/map')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <MapPin className="w-6 h-6 text-secondary mb-1" strokeWidth={2.5} />
            <p className="text-xs font-semibold">{t('quickActions.map')}</p>
          </button>

          <button
            data-testid="qa-coupons"
            onClick={() => navigate('/coupons')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">🎟️</span>
            <p className="text-xs font-semibold">{t('quickActions.coupons')}</p>
          </button>

          <button
            data-testid="qa-cashback"
            onClick={() => navigate('/cashback')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">💰</span>
            <p className="text-xs font-semibold">{t('quickActions.cashback')}</p>
          </button>

          <button
            data-testid="qa-info"
            onClick={() => navigate('/beach-info')}
            className="bg-white rounded-xl p-3 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex flex-col items-center"
          >
            <span className="text-2xl mb-1">📰</span>
            <p className="text-xs font-semibold">{t('quickActions.beach')}</p>
          </button>
        </div>

        {/* Referral Banner */}
        <button
          data-testid="referral-banner"
          onClick={() => navigate('/referral')}
          className="w-full bg-gradient-to-r from-accent to-primary rounded-2xl p-4 text-white shadow-lg flex items-center gap-3 active:scale-[0.98] transition-transform"
        >
          <div className="text-3xl">🎁</div>
          <div className="flex-1 text-left">
            <p className="font-['Outfit'] font-bold text-lg">{t('home.referralTitle')}</p>
            <p className="text-xs text-white/90">{t('home.referralSub')}</p>
          </div>
          <span className="text-white/80 text-xs">→</span>
        </button>

        {/* Quick Actions Row 2 */}
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="qa-loyalty"
            onClick={() => navigate('/loyalty')}
            className="bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex items-center gap-2"
          >
            <span className="text-2xl">⭐</span>
            <div className="text-left">
              <p className="text-sm font-semibold">{t('home.loyaltyTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('home.loyaltySub')}</p>
            </div>
          </button>
          <button
            data-testid="qa-orders"
            onClick={() => navigate('/orders')}
            className="bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow active:scale-95 flex items-center gap-2"
          >
            <Clock className="w-7 h-7 text-primary" strokeWidth={2.5} />
            <div className="text-left">
              <p className="text-sm font-semibold">{t('home.ordersTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('home.ordersSub')}</p>
            </div>
          </button>
        </div>

        {/* Nearby Kiosks */}
        {kiosks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-['Outfit'] text-xl font-bold text-foreground">
                {hasLocation ? t('home.nearby') : t('home.available')}
              </h2>
              <button
                onClick={() => navigate('/map')}
                className="text-primary text-sm font-semibold flex items-center gap-1"
              >
                {t('home.viewAll')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {kiosks.map((kiosk) => (
                <button
                  key={kiosk.id}
                  data-testid={`kiosk-card-${kiosk.id}`}
                  onClick={() => navigate(`/kiosk/${kiosk.id}`)}
                  className="w-full bg-white rounded-xl p-4 shadow-md border border-border hover:shadow-lg transition-shadow text-left active:scale-[0.98]"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl">🏚️</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">
                        {kiosk.display_name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {kiosk.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs text-secondary">
                          <MapPin className="w-3 h-3" />
                          {hasLocation ? t('home.kioskNearYou') : t('home.kioskAvailable')}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-primary">
                          <Star className="w-3 h-3 fill-current" />
                          {t('home.kioskOpen')}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Featured Products */}
        {featuredProducts.length > 0 && (
          <div>
            <h2 className="font-['Outfit'] text-xl font-bold text-foreground mb-3">
              {t('home.featured')}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl shadow-md border border-border overflow-hidden"
                >
                  {product.image_url && (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-32 object-cover"
                    />
                  )}
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-foreground line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-primary font-bold mt-1">
                      R$ {product.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Home;
